import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ScrollToTop } from '@/components/ScrollToTop';
import { AppShell } from '@/components/shell/AppShell';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { OnboardingLayout } from '@/components/onboarding/OnboardingLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { StartRoute } from '@/routes/StartRoute';
import { LibraryRoute } from '@/routes/LibraryRoute';
import { InspirationRoute } from '@/routes/InspirationRoute';
import { AccountRoute } from '@/routes/AccountRoute';
import { SubscriptionRoute } from '@/routes/account/SubscriptionRoute';
import { ChildProfileRoute as AccountChildProfileRoute } from '@/routes/account/ChildProfileRoute';
import { ChildrenRoute } from '@/routes/account/ChildrenRoute';
import { PasswordRoute } from '@/routes/account/PasswordRoute';
import { ProfileRoute } from '@/routes/account/ProfileRoute';
import { NotificationsRoute } from '@/routes/account/NotificationsRoute';
import { ExportRoute } from '@/routes/account/ExportRoute';
import { DeleteRoute } from '@/routes/account/DeleteRoute';
import { WithdrawalRoute } from '@/routes/account/WithdrawalRoute';
import { LoginRoute } from '@/routes/auth/LoginRoute';
import { SignupRoute } from '@/routes/auth/SignupRoute';
import { ForgotPasswordRoute } from '@/routes/auth/ForgotPasswordRoute';
import { ResetPasswordRoute } from '@/routes/auth/ResetPasswordRoute';
import { EmailSentRoute } from '@/routes/auth/EmailSentRoute';
import { VerifyTokenRoute } from '@/routes/auth/VerifyTokenRoute';
import { RedeemRoute } from '@/routes/gifts/RedeemRoute';
import { WelcomeRoute } from '@/routes/onboarding/WelcomeRoute';
import { ChildProfileRoute } from '@/routes/onboarding/ChildProfileRoute';
import { PushConsentRoute } from '@/routes/onboarding/PushConsentRoute';
import { WorldSelectionRoute } from '@/routes/onboarding/WorldSelectionRoute';
import { HowItWorksRoute } from '@/routes/onboarding/HowItWorksRoute';
import { AABuilderRoute } from '@/routes/builder/AABuilderRoute';
import { KPBuilderRoute } from '@/routes/builder/KPBuilderRoute';
import { SBBuilderRoute } from '@/routes/builder/SBBuilderRoute';
import { OutputRoute } from '@/routes/story/OutputRoute';
import { StoryRoute } from '@/routes/story/StoryRoute';
import { PdfViewerRoute } from '@/routes/story/PdfViewerRoute';
import { NowPlayingRoute } from '@/routes/story/NowPlayingRoute';
import { SeasonRoute } from '@/routes/series/SeasonRoute';
import { CatalogWorldRoute } from '@/routes/catalog/CatalogWorldRoute';
import { CatalogBrowseRoute } from '@/routes/catalog/CatalogBrowseRoute';
import { SERIES_ENABLED, CATALOG_ENABLED, BOOK_ENABLED } from '@/lib/config';
import { BookShell } from '@/features/book/components/BookShell';
import { PersonenRoute } from '@/features/book/routes/PersonenRoute';
import { FigurenRoute } from '@/features/probe/routes/FigurenRoute';
import { BookRoute } from '@/features/book/routes/BookRoute';
import { BestellungRoute } from '@/features/book/routes/BestellungRoute';
import { BestellungErfolgRoute } from '@/features/book/routes/BestellungErfolgRoute';
import { MeineBuecherRoute } from '@/features/book/routes/MeineBuecherRoute';
import BookReviewRoute from '@/features/book/review/BookReviewRoute';
import { CheckoutReviewRoute } from '@/routes/CheckoutReviewRoute';
import { CheckoutSuccessRoute } from '@/routes/CheckoutSuccessRoute';
import { CheckoutCancelRoute } from '@/routes/CheckoutCancelRoute';
import { NotFoundRoute } from '@/routes/NotFoundRoute';

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/auth/login" element={<LoginRoute />} />
          <Route path="/auth/signup" element={<SignupRoute />} />
          <Route path="/auth/forgot" element={<ForgotPasswordRoute />} />
          <Route path="/auth/reset" element={<ResetPasswordRoute />} />
          <Route path="/auth/confirm" element={<EmailSentRoute />} />
          <Route path="/auth/verify" element={<VerifyTokenRoute />} />
        </Route>
        {/*
          Book flow route branch (P5-5 route-fix 2026-08-06 —
          Sebastian ruling). Sits OUTSIDE ProtectedRoute because
          §A4 mandates the configurator is reachable without a
          session — "Konfigurieren komplett offen und kostenlos;
          Formularfelder erzeugen keine API-Kosten." Registration
          is a J-01 END-of-Schritt-1 gate (the LightGateOverlay),
          not an app-entry gate. A visitor who deep-links to /book
          must land on the configurator; the previous shape
          bounced him to /auth/login?returnTo=%2Fbook and made
          the whole Light-Gate design unreachable.

          `/book` is public (PersonenRoute holds transient photo
          state + localStorage-persisted text, no authenticated
          endpoint until the LightGateOverlay).
          `/book/:orderId` remains protected — an existing order
          belongs to an account. Nested ProtectedRoute inside the
          BookShell branch keeps the auth semantics for that leaf
          while sharing the shell layout with the public sibling.

          Route-registration gating on BOOK_ENABLED (per Order R3):
          when the flag folds to false at build time, this entire
          JSX subtree becomes dead code and Vite/Rollup drops the
          BookShell + book route modules from the bundle. Runtime
          hook gating (like useSeriesEnabled) would keep the modules
          in the bundle regardless.

          BookShell is a layout route from features/book/ (own
          shell — no TopBar, no BottomNav, no MiniPlayer, no
          EmailVerificationBanner per Order R2). Deliberately
          OUTSIDE OnboardingGuard (audio onboarding at
          /onboarding/world is audio-specific and would stall a
          book buyer mid-journey — ruling 2026-07-29).
        */}
        {BOOK_ENABLED && (
          <Route element={<BookShell />}>
            <Route path="/book" element={<PersonenRoute />} />
            {/* Probecover Stage 2 P5 — "/figuren" is PUBLIC (form
                first, account second). Logged-out visitors can fill
                the form; the submit handler fires a LightGate
                overlay for anonymous customers (email + password +
                Turnstile + optional marketing consent) and only
                creates the probe post-auth, pre-upload. The 2-per-
                account cap enforced by ProbeService.createProbe
                still fires AFTER register (first probe is free, the
                third fails cleanly with probe_cap_reached). Feature-
                flag gate `user.featureFlags.probecover` renders a
                Navigate to `/` only when a LOGGED-IN user with the
                flag off hits the route — logged-out visitors see
                the form unconditionally so a flag-off account can
                still land there via a mail link, hit the overlay,
                and sign up. */}
            <Route path="/figuren" element={<FigurenRoute />} />
            <Route element={<ProtectedRoute />}>
              {/* Sebastian ruling 2026-09-15 — Meine Bücher list.
                  Protected (customer's own book collection). Renders
                  under BookShell so header + logout + switcher are
                  inherited; no Schrittleiste (list page is a hub, not
                  a step). */}
              <Route path="/book/meine" element={<MeineBuecherRoute />} />
              {/*
                P5-4 Commit B — parameterised re-entry route.
                URL shape `/book/:orderId` (two segments, matching
                the `/story/:storyId` + `/output/:jobId` sibling
                pattern in the audio branch); NOT the three-segment
                backend shape `/book/orders/:orderId`. API-vs-UI
                path divergence is already established here
                (`/api/stories/:id` vs `/story/:storyId`). The
                Schrittleiste is rendered by BookShell via
                `useMatch('/book/:orderId')`.
              */}
              <Route path="/book/:orderId" element={<BookRoute />} />
              {/* 6-3 Bildschirm 5 · Bestellung (Sebastian ruling
                  2026-08-15). Sub-routes under the BookShell so
                  Schrittleiste + envelope context are inherited.
                  BestellungRoute renders when status is preview_ready
                  or checkout_pending (re-entry after Stripe
                  abandonment); other statuses redirect back to
                  /book/:orderId. BestellungErfolgRoute reads
                  session_id from Stripe's redirect + polls
                  checkout-status until webhook lands. */}
              <Route path="/book/:orderId/bestellung" element={<BestellungRoute />} />
              <Route path="/book/:orderId/erfolg" element={<BestellungErfolgRoute />} />
              {/* Book-arrived review + share landing. Reached from the
                  book-review follow-up mail; owner-only (rating POST
                  server-side guard). */}
              <Route path="/book/:bookOrderId/review" element={<BookReviewRoute />} />
            </Route>
          </Route>
        )}
        <Route element={<ProtectedRoute />}>
          <Route element={<OnboardingGuard mode="require-incomplete" />}>
            <Route element={<OnboardingLayout />}>
              <Route path="/onboarding/welcome" element={<WelcomeRoute />} />
              <Route path="/onboarding/child_profile" element={<ChildProfileRoute />} />
              <Route path="/onboarding/push" element={<PushConsentRoute />} />
              <Route path="/onboarding/world" element={<WorldSelectionRoute />} />
              <Route path="/onboarding/how-it-works/:world" element={<HowItWorksRoute />} />
            </Route>
          </Route>
          <Route element={<OnboardingGuard mode="require-complete" />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<StartRoute />} />
              <Route path="/library" element={<LibraryRoute />} />
              {/* Batch 10 Sub-phase D — bare /builder route removed
                  per Decision #13 Path A. Production CTAs that used to
                  navigate('/builder') now trigger WorldSelectionSheet
                  via `useWorldSelectionSheet().openSheet()` (see
                  AppShell + WorldSelectionSheetContext). World-specific
                  /builder/{slug} routes below stay — used by IdeaTile
                  navigation and direct deep-links. */}
              <Route path="/builder/aa" element={<AABuilderRoute />} />
              <Route path="/builder/kp" element={<KPBuilderRoute />} />
              <Route path="/builder/sb" element={<SBBuilderRoute />} />
              <Route path="/output/:jobId" element={<OutputRoute />} />
              <Route path="/story/:storyId" element={<StoryRoute />} />
              {/* Series season view (B3). Build-flag-gated: when
                  VITE_SERIES_ENABLED is false the route is absent and a
                  deep-link falls through to NotFoundRoute (feature dormant).
                  Backend capability still gates the data (GET /series/:id). */}
              {SERIES_ENABLED && <Route path="/series/:id" element={<SeasonRoute />} />}
              {/* Wissenswelten catalog world-detail (Block C2). Build-flag-gated
                  like series: when VITE_CATALOG_ENABLED is false the route is
                  absent (feature dormant); backend capability still gates the
                  data (GET /api/catalog/worlds/:slug/detail → 404 when off). */}
              {CATALOG_ENABLED && <Route path="/catalog" element={<CatalogBrowseRoute />} />}
              {CATALOG_ENABLED && <Route path="/catalog/:slug" element={<CatalogWorldRoute />} />}
              {/* PWA #16 Sub-Phase 16.6 — Homescreen-PWA in-app PDF
                  viewer. Browser-mode never visits this route (anchor
                  with target="_blank" opens native viewer in a new
                  tab). AppShell suppresses TopBar + BottomNav for this
                  path so the iframe owns the full viewport. */}
              <Route path="/story/:storyId/pdf" element={<PdfViewerRoute />} />
              <Route path="/inspiration" element={<InspirationRoute />} />
              {/* P14 Block 4 (2026-09-23) — gift-code redeem page.
                  Inside ProtectedRoute so a code-in-URL (?code=…)
                  from the mail or QR scan preserves the query
                  through login via ProtectedRoute's returnTo. */}
              <Route path="/einloesen" element={<RedeemRoute />} />
              <Route path="/account" element={<AccountRoute />} />
              <Route path="/account/subscription" element={<SubscriptionRoute />} />
              <Route path="/account/children" element={<ChildrenRoute />} />
              <Route path="/account/child_profile" element={<AccountChildProfileRoute />} />
              <Route path="/account/password" element={<PasswordRoute />} />
              <Route path="/account/profile" element={<ProfileRoute />} />
              <Route path="/account/notifications" element={<NotificationsRoute />} />
              <Route path="/account/export" element={<ExportRoute />} />
              <Route path="/account/delete" element={<DeleteRoute />} />
              <Route
                path="/account/widerruf/:contractType/:contractId"
                element={<WithdrawalRoute />}
              />
              <Route path="/checkout/review" element={<CheckoutReviewRoute />} />
              <Route path="/checkout/success" element={<CheckoutSuccessRoute />} />
              <Route path="/checkout/cancel" element={<CheckoutCancelRoute />} />
              {/* Now-Playing stays INSIDE AppShell so the singleton
                  <audio> element + MediaSession hook + bindAudio ref
                  survive the navigation. AppShell hides its own chrome
                  (TopBar/BottomNav/MiniPlayer/banner) on this route so
                  NowPlayingPage owns the full viewport. */}
              <Route path="/now-playing" element={<NowPlayingRoute />} />
              {/* Batch 12 Sub-phase C — 404 fallback. Last route
                  inside AppShell so chrome (TopBar + BottomNav)
                  stays visible per Briefing Kap. 11 line 1555.
                  Catches authenticated-but-bad-path navigations.
                  Outer-level fallback for /auth/* + /onboarding/*
                  unmatched paths tracked as PWA Offen #59. */}
              <Route path="*" element={<NotFoundRoute />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
